
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Accordion, Container, Table, Button, Modal, Form, Alert, Pagination, InputGroup } from 'react-bootstrap';
import { accountHolderService } from '../services/apiClient';
import { notificationService } from '../services/notificationService';
import ConfirmationModal from '../components/ConfirmationModal';
import LoadingIndicator from '../components/LoadingIndicator';
import { AccountHolder, AccountHolderAddressFee, AccountHolderCreditNote, CreateAccountHolderRequest } from '../types/index';
import { useLocalization } from '../context/LocalizationContext';
import './AccountHolders.css';
import 'react-quill/dist/quill.snow.css';
import ReactQuill from 'react-quill';



export const AccountHolders: React.FC = () => {
  const { t, formatDate, formatCurrency, formatMonthYear } = useLocalization();
  const tRef = useRef(t);
  const [holders, setHolders] = useState<AccountHolder[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showMonthlyFeeHistoryModal, setShowMonthlyFeeHistoryModal] = useState(false);
  const [showCreditNotesModal, setShowCreditNotesModal] = useState(false);
  const [historyModalHolder, setHistoryModalHolder] = useState<AccountHolder | null>(null);
  const [creditNotesModalHolder, setCreditNotesModalHolder] = useState<AccountHolder | null>(null);
  const [historyModalCurrentPage, setHistoryModalCurrentPage] = useState(1);
  const [historyModalPageSize, setHistoryModalPageSize] = useState(10);
  const [creditNotesCurrentPage, setCreditNotesCurrentPage] = useState(1);
  const [creditNotesPageSize, setCreditNotesPageSize] = useState(10);
  const [editingCreditNoteId, setEditingCreditNoteId] = useState<string | null>(null);
  const [creditNoteAmount, setCreditNoteAmount] = useState('');
  const [creditNoteDescription, setCreditNoteDescription] = useState('');
  const [creditNoteSubmitting, setCreditNoteSubmitting] = useState(false);
  const creditNoteAmountInputRef = useRef<HTMLInputElement | null>(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBodyHtml, setEmailBodyHtml] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [selectedRecipientIds, setSelectedRecipientIds] = useState<number[]>([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [showDraftWarning, setShowDraftWarning] = useState(false);
  const [showRestorePrompt, setShowRestorePrompt] = useState(false);

  // Restore selectedAccountHolder for personalized greeting
  const selectedAccountHolder = !sendToAll && selectedRecipientIds.length === 1
    ? holders.find(h => h.id === selectedRecipientIds[0])
    : undefined;

  const hasUnsavedChanges = useMemo(() => {
    const bodyText = emailBodyHtml.replace(/<[^>]*>/g, '').trim();
    return (
      emailSubject.trim() !== '' ||
      bodyText !== '' ||
      sendToAll ||
      selectedRecipientIds.length > 0
    );
  }, [emailSubject, emailBodyHtml, sendToAll, selectedRecipientIds]);

  const saveEmailDraft = () => {
    try {
      const draft = {
        subject: emailSubject,
        bodyHtml: emailBodyHtml,
        sendToAll,
        selectedRecipientIds,
      };
      localStorage.setItem('emailDraft', JSON.stringify(draft));
      notificationService.info(t('draftSaved'));
    } catch {
      // ignore
    }
  };

  const clearEmailDraft = () => {
    localStorage.removeItem('emailDraft');
  };

  const loadEmailDraft = () => {
    try {
      const raw = localStorage.getItem('emailDraft');
      if (raw) {
        const d = JSON.parse(raw);
        setEmailSubject(d.subject || '');
        setEmailBodyHtml(d.bodyHtml || '');
        setSendToAll(d.sendToAll || false);
        setSelectedRecipientIds(d.selectedRecipientIds || []);
      }
    } catch {
      /* ignore */
    }
  };

  const handleAttemptClose = () => {
    if (sendingEmail) return;
    if (hasUnsavedChanges) {
      setShowDraftWarning(true);
    } else {
      clearEmailDraft();
      setShowEmailModal(false);
    }
  };

  useEffect(() => {
    if (showEmailModal) {
      // initialize state when modal opens
      setEmailSubject('');
      setEmailBodyHtml('');
      setSendToAll(false);
      setSelectedRecipientIds([]);
      // setRecipientSearchTerm('');

      const raw = localStorage.getItem('emailDraft');
      if (raw) {
        // show styled confirmation instead of window.confirm
        setShowRestorePrompt(true);
      }
    }
  }, [showEmailModal]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [accountFormValidated, setAccountFormValidated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formData, setFormData] = useState<CreateAccountHolderRequest>({
    name: '',
    email: '',
    emails: [''],
    phoneNumber: '',
    address: '',
    addresses: [''],
    addressFees: [{ address: '', monthlyFee: 0 }],
    monthlyFee: 0,
    keywords: [],
    isActive: true,
  });
  const [keywordsInput, setKeywordsInput] = useState('');

  // Confirmation modal state
  const [isSaving, setIsSaving] = useState(false);

  // Confirmation modal state
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState<{
    title: string;
    message: string;
    onConfirm: () => void;
    variant?: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    cancelText?: string;
    isLoading?: boolean;
  } | null>(null);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const loadAccountHolders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await accountHolderService.getAll();
      setHolders(data);
    } catch (err) {
      setError(tRef.current('failedToLoadAccountHolders'));
      console.error(err);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, []);

  const replaceHolderInState = useCallback((updatedHolder: AccountHolder) => {
    setHolders((previous) => previous.map((holder) => (holder.id === updatedHolder.id ? updatedHolder : holder)));
    setHistoryModalHolder((previous) => (previous?.id === updatedHolder.id ? updatedHolder : previous));
    setCreditNotesModalHolder((previous) => (previous?.id === updatedHolder.id ? updatedHolder : previous));
  }, []);

  useEffect(() => {
    loadAccountHolders();
  }, [loadAccountHolders]);

  const getHolderEmails = useCallback((holder: Pick<AccountHolder, 'email' | 'emails'>): string[] => {
    return ([...(holder.emails || []), holder.email])
      .map((email) => (email || '').trim())
      .filter((email, index, values) => email.length > 0 && values.findIndex((value) => value.toLowerCase() === email.toLowerCase()) === index);
  }, []);

  const getHolderAddressFees = useCallback((holder: Pick<AccountHolder, 'address' | 'addresses' | 'addressFees' | 'monthlyFee'>): AccountHolderAddressFee[] => {
    const normalizedFromAddressFees = (holder.addressFees || [])
      .map((item) => ({
        address: (item.address || '').trim(),
        monthlyFee: Math.max(0, Number(item.monthlyFee) || 0),
      }))
      .filter((item) => item.address.length > 0);

    if (normalizedFromAddressFees.length > 0) {
      return normalizedFromAddressFees
        .filter((item, index, values) => values.findIndex((candidate) => candidate.address.toLowerCase() === item.address.toLowerCase()) === index)
        .map((item) => ({ ...item, monthlyFee: Number(item.monthlyFee.toFixed(2)) }));
    }

    const normalizedAddresses = [...(holder.addresses || []), holder.address]
      .map((address) => address.trim())
      .filter((address, index, values) => address.length > 0 && values.findIndex((value) => value.toLowerCase() === address.toLowerCase()) === index);

    const fallbackFee = Number((holder.monthlyFee || 0).toFixed(2));
    return normalizedAddresses.map((address) => ({ address, monthlyFee: fallbackFee }));
  }, []);

  const getHolderMonthlyFeeHistory = useCallback((holder: AccountHolder) => {
    const normalizeHistoryAddressFees = (addressFees: AccountHolderAddressFee[], fallbackMonthlyFee: number): AccountHolderAddressFee[] => {
      const normalized = (addressFees || [])
        .map((item) => ({
          address: (item.address || '').trim(),
          monthlyFee: Math.max(0, Number(item.monthlyFee) || 0),
        }))
        .filter((item) => item.address.length > 0);

      if (normalized.length > 0) {
        return normalized
          .filter((item, index, values) => values.findIndex((candidate) => candidate.address.toLowerCase() === item.address.toLowerCase()) === index)
          .map((item) => ({ ...item, monthlyFee: Number(item.monthlyFee.toFixed(2)) }));
      }

      const fallbackAddressFees = getHolderAddressFees(holder);
      if (fallbackAddressFees.length > 0) {
        return fallbackAddressFees;
      }

      const fallbackAddress = (holder.address || '').trim();
      return fallbackAddress ? [{ address: fallbackAddress, monthlyFee: Number(Math.max(0, fallbackMonthlyFee || 0).toFixed(2)) }] : [];
    };

    const normalizedHistory = (holder.monthlyFeeHistory || [])
      .map((entry) => {
        const addressFees = normalizeHistoryAddressFees(entry.addressFees || [], entry.totalMonthlyFee || holder.monthlyFee || 0);
        return {
          effectiveFromMonth: entry.effectiveFromMonth,
          changedAt: entry.changedAt,
          addressFees,
          totalMonthlyFee: Number((addressFees.reduce((sum, item) => sum + item.monthlyFee, 0) || entry.totalMonthlyFee || 0).toFixed(2)),
        };
      })
      .filter((entry) => !!entry.effectiveFromMonth)
      .sort((first, second) => new Date(second.effectiveFromMonth).getTime() - new Date(first.effectiveFromMonth).getTime());

    if (normalizedHistory.length > 0) {
      return normalizedHistory;
    }

    const fallbackAddressFees = getHolderAddressFees(holder);
    return [{
      effectiveFromMonth: holder.createdDate,
      changedAt: holder.createdDate,
      addressFees: fallbackAddressFees,
      totalMonthlyFee: Number((fallbackAddressFees.reduce((sum, item) => sum + item.monthlyFee, 0) || holder.monthlyFee || 0).toFixed(2)),
    }];
  }, [getHolderAddressFees]);

  const historyModalEntries = useMemo(() => {
    if (!historyModalHolder) return [];
    return getHolderMonthlyFeeHistory(historyModalHolder);
  }, [historyModalHolder, getHolderMonthlyFeeHistory]);

  const hasHolderMonthlyFeeHistory = useCallback((holder: AccountHolder): boolean => {
    const entries = (holder.monthlyFeeHistory || []).filter((entry) => !!entry?.effectiveFromMonth);
    return entries.length > 1;
  }, []);

  const historyModalTotalPages = Math.max(1, Math.ceil(historyModalEntries.length / historyModalPageSize));
  const safeHistoryModalCurrentPage = Math.min(historyModalCurrentPage, historyModalTotalPages);
  const pagedHistoryModalEntries = useMemo(() => {
    const startIndex = (safeHistoryModalCurrentPage - 1) * historyModalPageSize;
    return historyModalEntries.slice(startIndex, startIndex + historyModalPageSize);
  }, [historyModalEntries, safeHistoryModalCurrentPage, historyModalPageSize]);
  const historyModalPageStart = historyModalEntries.length === 0 ? 0 : (safeHistoryModalCurrentPage - 1) * historyModalPageSize + 1;
  const historyModalPageEnd = Math.min(safeHistoryModalCurrentPage * historyModalPageSize, historyModalEntries.length);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize, holders.length]);

  const handleShowModal = (holder?: AccountHolder) => {
    setAccountFormValidated(false);

    if (holder) {
      const holderAddressFees = getHolderAddressFees(holder);
      const holderEmails = ((holder.emails || []).length > 0 ? holder.emails : [holder.email])
        .map((email) => (email || '').trim())
        .filter((email, index, values) => email.length > 0 && values.findIndex((value) => value.toLowerCase() === email.toLowerCase()) === index);
      setEditingId(holder.id);
      setFormData({
        name: holder.name,
        email: holderEmails[0] || '',
        emails: holderEmails.length > 0 ? holderEmails : [''],
        phoneNumber: holder.phoneNumber,
        address: holder.address,
        addresses: holderAddressFees.map((item) => item.address),
        addressFees: holderAddressFees.length > 0 ? holderAddressFees : [{ address: '', monthlyFee: 0 }],
        monthlyFee: Number((holderAddressFees.reduce((sum, item) => sum + item.monthlyFee, 0) || holder.monthlyFee || 0).toFixed(2)),
        keywords: holder.keywords || [],
        isActive: holder.isActive,
      });
      setKeywordsInput((holder.keywords || []).join(', '));
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        email: '',
        emails: [''],
        phoneNumber: '',
        address: '',
        addresses: [''],
        addressFees: [{ address: '', monthlyFee: 0 }],
        monthlyFee: 0,
        keywords: [],
        isActive: true,
      });
      setKeywordsInput('');
    }
    setShowModal(true);
  };


  const handleCloseModal = () => {
      if (isSaving) return;
    setShowModal(false);
    setEditingId(null);
    setAccountFormValidated(false);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const fieldValue =
      e.target instanceof HTMLInputElement && e.target.type === 'checkbox'
        ? e.target.checked
        : value;

    setFormData((prev) => ({
      ...prev,
      [name]: fieldValue,
    }));
  };

  const handleAddressChange = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      addressFees: prev.addressFees.map((item, addressIndex) => addressIndex === index
        ? { ...item, address: value }
        : item),
    }));
  };

  const handleAddressMonthlyFeeChange = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      addressFees: prev.addressFees.map((item, addressIndex) => addressIndex === index
        ? { ...item, monthlyFee: Math.max(0, Number.parseFloat(value || '0') || 0) }
        : item),
    }));
  };

  const handleAddAddressField = () => {
    setFormData((prev) => ({
      ...prev,
      addressFees: [...prev.addressFees, { address: '', monthlyFee: 0 }],
    }));
  };

  const handleRemoveAddressField = (index: number) => {
    setFormData((prev) => {
      const nextAddressFees = prev.addressFees.filter((_, addressIndex) => addressIndex !== index);

      return {
        ...prev,
        addressFees: nextAddressFees.length > 0 ? nextAddressFees : [{ address: '', monthlyFee: 0 }],
      };
    });
  };

  const isValidEmail = (value: string): boolean => /^\S+@\S+\.\S+$/.test(value.trim());

  const handleEmailChange = (index: number, value: string) => {
    setFormData((prev) => ({
      ...prev,
      emails: prev.emails.map((email, emailIndex) => (emailIndex === index ? value : email)),
    }));
  };

  const handleAddEmailField = () => {
    setFormData((prev) => ({
      ...prev,
      emails: [...(prev.emails || []), ''],
    }));
  };

  const handleRemoveEmailField = (index: number) => {
    setFormData((prev) => {
      const nextEmails = (prev.emails || []).filter((_, emailIndex) => emailIndex !== index);
      return {
        ...prev,
        emails: nextEmails.length > 0 ? nextEmails : [''],
      };
    });
  };

  const duplicateEmailIndexes = useMemo(() => {
    const duplicates = new Set<number>();
    const seen = new Map<string, number>();

    (formData.emails || []).forEach((email, index) => {
      const normalized = (email || '').trim().toLowerCase();
      if (!normalized) {
        return;
      }

      const firstIndex = seen.get(normalized);
      if (firstIndex === undefined) {
        seen.set(normalized, index);
        return;
      }

      duplicates.add(firstIndex);
      duplicates.add(index);
    });

    return duplicates;
  }, [formData.emails]);

  const hasBlankEmailRows = useMemo(() => {
    return (formData.emails || []).some((email) => !(email || '').trim());
  }, [formData.emails]);

  const hasInvalidEmailRows = useMemo(() => {
    return (formData.emails || [])
      .filter((email) => (email || '').trim().length > 0)
      .some((email) => !isValidEmail(email));
  }, [formData.emails]);

  const hasDuplicateEmailRows = duplicateEmailIndexes.size > 0;

  const duplicateAddressIndexes = useMemo(() => {
    const duplicates = new Set<number>();
    const seen = new Map<string, number>();

    formData.addressFees.forEach((item, index) => {
      const normalized = (item.address || '').trim().toLowerCase();
      if (!normalized) {
        return;
      }

      const firstIndex = seen.get(normalized);
      if (firstIndex === undefined) {
        seen.set(normalized, index);
        return;
      }

      duplicates.add(firstIndex);
      duplicates.add(index);
    });

    return duplicates;
  }, [formData.addressFees]);

  const hasBlankAddressRows = useMemo(() => {
    return formData.addressFees.some((item) => !(item.address || '').trim());
  }, [formData.addressFees]);

  const hasDuplicateAddressRows = duplicateAddressIndexes.size > 0;

  const isAccountHolderFormValid = (): boolean => {
    return formData.name.trim().length > 0
      && !hasBlankEmailRows
      && !hasInvalidEmailRows
      && !hasDuplicateEmailRows
      && !hasBlankAddressRows
      && !hasDuplicateAddressRows;
  };

  const handleSaveAccount = async () => {
    setAccountFormValidated(true);

    if (!formData.name.trim()) {
      notificationService.error(t('pleaseCompleteRequiredFields'));
      return;
    }

    if (hasBlankEmailRows) {
      notificationService.error(t('pleaseCompleteRequiredFields'));
      return;
    }

    if (hasInvalidEmailRows) {
      notificationService.error(t('validEmailRequired'));
      return;
    }

    if (hasDuplicateEmailRows) {
      notificationService.error(t('duplicateEmailsNotAllowed'));
      return;
    }

    if (hasBlankAddressRows) {
      notificationService.error(t('addressLineCannotBeBlank'));
      return;
    }

    if (hasDuplicateAddressRows) {
      notificationService.error(t('duplicateAddressesNotAllowed'));
      return;
    }

    try {
        setIsSaving(true);
      const normalizedKeywords = keywordsInput
        .split(',')
        .map((keyword) => keyword.trim())
        .filter((keyword) => keyword.length > 0);

      const normalizedAddressFees = formData.addressFees
        .map((item) => ({
          address: item.address.trim(),
          monthlyFee: Number(Math.max(0, item.monthlyFee || 0).toFixed(2)),
        }))
        .filter((item) => item.address.length > 0)
        .filter((item, index, values) => values.findIndex((candidate) => candidate.address.toLowerCase() === item.address.toLowerCase()) === index);

      const normalizedAddresses = normalizedAddressFees.map((item) => item.address);
      const totalMonthlyFee = Number(normalizedAddressFees.reduce((sum, item) => sum + item.monthlyFee, 0).toFixed(2));
      const normalizedEmails = (formData.emails || [])
        .map((email) => (email || '').trim())
        .filter((email) => email.length > 0)
        .filter((email, index, values) => values.findIndex((candidate) => candidate.toLowerCase() === email.toLowerCase()) === index);

      const payload: CreateAccountHolderRequest = {
        ...formData,
        name: formData.name.trim(),
        email: normalizedEmails[0] || '',
        emails: normalizedEmails,
        phoneNumber: formData.phoneNumber.trim(),
        address: normalizedAddresses[0] || '',
        addresses: normalizedAddresses,
        addressFees: normalizedAddressFees,
        monthlyFee: totalMonthlyFee,
        keywords: normalizedKeywords,
      };

      if (editingId) {
        await accountHolderService.update(editingId, payload);
        notificationService.success(t('accountHolderUpdatedSuccessfully'));
      } else {
        await accountHolderService.create(payload);
        notificationService.success(t('accountHolderCreatedSuccessfully'));
      }
      handleCloseModal();
      loadAccountHolders();
    } catch (err) {
      notificationService.error(t('failedToSaveAccountHolder'));
      console.error(err);
      } finally {
        setIsSaving(false);
    }
  };

  const handleToggleStatus = async (holder: AccountHolder) => {
    const isActivating = !holder.isActive;
    setConfirmationConfig({
      title: isActivating ? t('activateAccountHolderTitle') : t('deactivateAccountHolderTitle'),
      message: isActivating ? t('activateAccountHolderConfirmMessage') : t('deactivateAccountHolderConfirmMessage'),
      variant: isActivating ? 'success' : 'warning',
      confirmText: isActivating ? t('activate') : t('deactivate'),
      onConfirm: async () => {
        try {
          await accountHolderService.toggleStatus(holder.id);
          notificationService.success(isActivating ? t('accountHolderActivatedSuccessfully') : t('accountHolderDeactivatedSuccessfully'));
          setShowConfirmation(false);
          loadAccountHolders();
        } catch (err) {
          notificationService.error(isActivating ? t('failedToActivateAccountHolder') : t('failedToDeactivateAccountHolder'));
          console.error(err);
        }
      },
    });
    setShowConfirmation(true);
  };

  const totalPages = Math.max(1, Math.ceil(holders.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const activeCreditTotalByHolder = useMemo(() => {
    const map = new Map<number, number>();
    holders.forEach((holder) => {
      const total = (holder.creditNotes || [])
        .reduce((sum, note) => sum + note.amount, 0);
      map.set(holder.id, total);
    });
    return map;
  }, [holders]);

  const pagedCreditNotes = useMemo(() => {
    if (!creditNotesModalHolder) return [] as AccountHolderCreditNote[];
    const notes = [...(creditNotesModalHolder.creditNotes || [])].sort((first, second) => (
      new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
    ));
    const safePage = Math.min(creditNotesCurrentPage, Math.max(1, Math.ceil(notes.length / creditNotesPageSize)));
    const startIndex = (safePage - 1) * creditNotesPageSize;
    return notes.slice(startIndex, startIndex + creditNotesPageSize);
  }, [creditNotesModalHolder, creditNotesCurrentPage, creditNotesPageSize]);

  const creditNotesTotal = creditNotesModalHolder?.creditNotes?.length || 0;
  const creditNotesTotalPages = Math.max(1, Math.ceil(creditNotesTotal / creditNotesPageSize));
  const safeCreditNotesCurrentPage = Math.min(creditNotesCurrentPage, creditNotesTotalPages);
  const creditNotesPageStart = creditNotesTotal === 0 ? 0 : (safeCreditNotesCurrentPage - 1) * creditNotesPageSize + 1;
  const creditNotesPageEnd = Math.min(safeCreditNotesCurrentPage * creditNotesPageSize, creditNotesTotal);

  const handleOpenCreditNotesModal = (holder: AccountHolder) => {
    setCreditNotesModalHolder(holder);
    setCreditNotesCurrentPage(1);
    setEditingCreditNoteId(null);
    setCreditNoteAmount('');
    setCreditNoteDescription('');
    setShowCreditNotesModal(true);
  };

  useEffect(() => {
    if (!showCreditNotesModal || creditNoteSubmitting) {
      return;
    }

    const focusTimeoutId = window.setTimeout(() => {
      creditNoteAmountInputRef.current?.focus();
      creditNoteAmountInputRef.current?.select();
    }, 0);

    return () => {
      window.clearTimeout(focusTimeoutId);
    };
  }, [showCreditNotesModal, creditNoteSubmitting, creditNotesModalHolder?.id]);

  const handleAddCreditNote = async () => {
    if (!creditNotesModalHolder) {
      return;
    }

    const normalizedAmountInput = creditNoteAmount.trim();
    const parsedAmount = normalizedAmountInput.length > 0 ? Number.parseFloat(normalizedAmountInput) : 0;
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      notificationService.error(t('amountCannotBeNegative'));
      return;
    }

    if (!creditNoteDescription.trim()) {
      notificationService.error(t('fieldRequired'));
      return;
    }

    try {
      setCreditNoteSubmitting(true);
      const payload = {
        description: creditNoteDescription.trim(),
        ...(normalizedAmountInput.length > 0 ? { amount: parsedAmount } : {}),
      };
      const updated = editingCreditNoteId
        ? await accountHolderService.updateCreditNote(creditNotesModalHolder.id, editingCreditNoteId, payload)
        : await accountHolderService.addCreditNote(creditNotesModalHolder.id, payload);

      replaceHolderInState(updated);
      setEditingCreditNoteId(null);
      setCreditNoteAmount('');
      setCreditNoteDescription('');
      notificationService.success(editingCreditNoteId ? t('noteUpdatedSuccessfully') : t('noteAddedSuccessfully'));
    } catch (err) {
      notificationService.error(editingCreditNoteId ? t('failedToUpdateNote') : t('failedToAddNote'));
      console.error(err);
    } finally {
      setCreditNoteSubmitting(false);
    }
  };

  const handleStartEditCreditNote = (creditNote: AccountHolderCreditNote) => {
    setEditingCreditNoteId(creditNote.id);
    setCreditNoteDescription(creditNote.description || '');
    setCreditNoteAmount(creditNote.amount > 0 ? String(creditNote.amount) : '');
  };

  const handleCancelEditCreditNote = () => {
    setEditingCreditNoteId(null);
    setCreditNoteAmount('');
    setCreditNoteDescription('');
  };

  const handleRequestDeleteCreditNote = (creditNote: AccountHolderCreditNote) => {
    if (!creditNotesModalHolder) {
      return;
    }

    setConfirmationConfig({
      title: t('deleteNoteTitle'),
      message: t('deleteNoteConfirmMessage'),
      variant: 'warning',
      confirmText: t('delete'),
      onConfirm: async () => {
        try {
          setCreditNoteSubmitting(true);
          const updated = await accountHolderService.deleteCreditNote(creditNotesModalHolder.id, creditNote.id);
          replaceHolderInState(updated);
          if (editingCreditNoteId === creditNote.id) {
            handleCancelEditCreditNote();
          }
          setShowConfirmation(false);
          notificationService.success(t('noteDeletedSuccessfully'));
        } catch (err) {
          notificationService.error(t('failedToDeleteNote'));
          console.error(err);
        } finally {
          setCreditNoteSubmitting(false);
        }
      },
    });
    setShowConfirmation(true);
  };
  const holderStats = useMemo(() => {
    const activeCount = holders.filter((holder) => holder.isActive).length;
    const inactiveCount = holders.length - activeCount;

    return {
      total: holders.length,
      active: activeCount,
      inactive: inactiveCount,
    };
  }, [holders]);

  const pagedHolders = useMemo(() => {
    const startIndex = (safeCurrentPage - 1) * pageSize;
    return holders.slice(startIndex, startIndex + pageSize);
  }, [holders, safeCurrentPage, pageSize]);
  const pageStart = holders.length === 0 ? 0 : (safeCurrentPage - 1) * pageSize + 1;
  const pageEnd = Math.min(safeCurrentPage * pageSize, holders.length);

  if (loading && !hasLoadedOnce) {
    return (
      <Container className="py-5 page-loading-shell">
        <div className="page-loading-header">
          <div className="page-loading-title page-skeleton-shimmer" aria-hidden="true" />
          <div className="page-loading-subtitle">
            <LoadingIndicator label={t('loading')} className="system-loading-status-card-inline" showLines={false} />
          </div>
        </div>
        <div className="row g-4">
          <div className="col-lg-5 col-md-6">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-lg-7 col-md-6">
            <div className="page-loading-block page-loading-block-tall page-skeleton-shimmer" aria-hidden="true" />
          </div>
          <div className="col-12">
            <div className="page-loading-block page-skeleton-shimmer" aria-hidden="true" />
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container className="py-5 account-holders-page" aria-busy={loading || isSaving || sendingEmail}>
      <div className="account-holders-hero page-header-panel">
        <div className="account-holders-header">
          <div>
            <h1 className="mb-1 page-header-title">
              <span className="page-header-title-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                  <circle cx="9" cy="8" r="3" fill="none" stroke="currentColor" strokeWidth="1.9" />
                  <path d="M3.7 18a5.6 5.6 0 0 1 10.6 0" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                  <circle cx="17.4" cy="9.4" r="2.4" fill="none" stroke="currentColor" strokeWidth="1.9" />
                  <path d="M15 18a4.6 4.6 0 0 1 5.1-3.7" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                </svg>
              </span>
              {t('accountHoldersTitle')}
            </h1>
          </div>
        </div>
      </div>

      <div className="account-holders-kpi-grid mb-4">
        <div className="account-holders-kpi-card account-holders-kpi-total">
          <div className="account-holders-kpi-label">{t('total')}</div>
          <div className="account-holders-kpi-value">{holderStats.total}</div>
        </div>
        <div className="account-holders-kpi-card account-holders-kpi-active">
          <div className="account-holders-kpi-label">{t('active')}</div>
          <div className="account-holders-kpi-value">{holderStats.active}</div>
        </div>
        <div className="account-holders-kpi-card account-holders-kpi-inactive">
          <div className="account-holders-kpi-label">{t('inactive')}</div>
          <div className="account-holders-kpi-value">{holderStats.inactive}</div>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="account-holders-actions-strip mb-4">
        <div className="d-flex align-items-center gap-2 account-holders-header-actions">
          <Button
            variant="light"
            className="account-holders-action-btn account-holders-action-btn-icon"
            onClick={() => setShowEmailModal(true)}
            aria-label={t('sendEmail')}
            title={t('sendEmail')}
          >
            <span className="account-holders-action-btn-label">{t('sendEmail')}</span>
            <svg viewBox="0 0 24 24" className="account-holders-action-btn-icon-glyph" focusable="false" aria-hidden="true">
              <rect x="3" y="5" width="18" height="14" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.9" />
              <path d="M4.8 7l7.2 6 7.2-6" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Button>
          <Button
            variant="light"
            className="account-holders-action-btn account-holders-action-btn-icon"
            onClick={() => handleShowModal()}
            aria-label={t('addNewAccountHolder')}
            title={t('addNewAccountHolder')}
          >
            <span className="account-holders-action-btn-label">{t('addNewAccountHolder')}</span>
            <svg viewBox="0 0 24 24" className="account-holders-action-btn-icon-glyph" focusable="false" aria-hidden="true">
              <circle cx="9" cy="7.2" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.9" />
              <path d="M3.3 16.4a5.9 5.9 0 0 1 11.8 0" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
              <circle cx="18" cy="17.1" r="3.6" fill="none" stroke="currentColor" strokeWidth="1.9" />
              <path d="M18 15.5v3.2M16.4 17.1h3.2" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
            </svg>
          </Button>
        </div>
      </div>

      <div className="account-holders-section-panel mb-4">
        {holders.length === 0 && !loading ? (
          <Alert variant="info" className="mb-0">{t('noAccountHoldersFound')}</Alert>
        ) : (
          <>
            {loading ? (
              <div className="text-center py-4 text-muted">
                <div className="dashboard-grid-loading-message">
                  <LoadingIndicator label={t('loading')} subtle />
                </div>
              </div>
            ) : (
              <Accordion alwaysOpen className="account-holders-address-accordion mb-3">
                {pagedHolders.map((holder, holderIndex) => (
                  <Accordion.Item
                    eventKey={`${holderIndex}`}
                    key={holder.id}
                    className={`account-holders-address-group ${holder.isActive ? '' : 'account-holders-address-group-inactive'}`.trim()}
                  >
                    <Accordion.Header>
                      <div className="account-holders-address-group-header">
                        <div>
                          <div className="account-holders-address-group-title">{holder.name}</div>
                          <div className="account-holders-address-group-meta">{formatCurrency(getHolderAddressFees(holder).reduce((sum, item) => sum + item.monthlyFee, 0))} / mo • {(holder.isActive ? t('active') : t('inactive'))}</div>
                        </div>
                      </div>
                    </Accordion.Header>
                    <Accordion.Body>
                      <div className="table-responsive account-holders-table-wrapper account-holders-grid-shell">
                        <Table className="account-holders-grid-table mb-0">
                          <thead>
                            <tr>
                              <th>{t('name')}</th>
                              <th>{t('email')}</th>
                              <th>{t('phoneNumber')}</th>
                              <th>{t('linkedAddressesWithFees')}</th>
                              <th>{t('monthlyFee')}</th>
                              <th>{t('keywords')}</th>
                              <th>{t('createdDateLabel')}</th>
                              <th>{t('actions')}</th>
                              <th className="account-holders-edit-column-header" aria-label={t('edit')}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {(() => {
                              const holderAddressFees = getHolderAddressFees(holder);
                              const holderEmails = getHolderEmails(holder);
                              const visibleHolderEmails = holderEmails.slice(0, 3);
                              const hiddenEmailCount = Math.max(0, holderEmails.length - visibleHolderEmails.length);
                              const holderMonthlyFeeTotal = holderAddressFees.reduce((sum, item) => sum + item.monthlyFee, 0);
                              const activeCreditTotal = activeCreditTotalByHolder.get(holder.id) || 0;

                              return (
                            <tr>
                                <td>{holder.name}</td>
                                <td>
                                  <div className="account-holders-email-chip-list">
                                    {holderEmails.length > 0
                                      ? visibleHolderEmails.map((email, emailIndex) => (
                                        <span key={`${holder.id}-email-${emailIndex}`} className="account-holders-email-chip">
                                          <span className="account-holders-email-chip-icon" aria-hidden="true">
                                            <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                              <rect x="3.2" y="5.6" width="17.6" height="12.8" rx="2.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
                                              <path d="M4.8 7.4L12 13l7.2-5.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                          </span>
                                          {email}
                                        </span>
                                      ))
                                      : '-'}
                                    {hiddenEmailCount > 0 && (
                                      <span
                                        className="account-holders-email-chip account-holders-email-chip-more"
                                        title={holderEmails.slice(visibleHolderEmails.length).join(', ')}
                                      >
                                        +{hiddenEmailCount} more
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>{holder.phoneNumber || '-'}</td>
                                <td>
                                  <div className="account-holder-address-lines">
                                    {holderAddressFees.length > 0 ? holderAddressFees.map((item, index) => (
                                      <div key={`${holder.id}-${item.address}-${index}`} className={`account-holder-address-line ${index === 0 ? 'is-primary' : ''}`}>
                                        <span className="account-holder-address-line-text">{item.address}</span>
                                        <span className="account-holder-address-line-fee">{formatCurrency(item.monthlyFee)}</span>
                                      </div>
                                    )) : '-'}
                                  </div>
                                </td>
                                <td className="fw-semibold">
                                  <div className="d-flex flex-column gap-1">
                                    <div className="d-inline-flex align-items-center gap-2">
                                      <span>{formatCurrency(holderMonthlyFeeTotal)}</span>
                                      {hasHolderMonthlyFeeHistory(holder) && (
                                        <Button
                                          variant="outline-secondary"
                                          size="sm"
                                          className="account-holders-history-icon-btn"
                                          onClick={() => {
                                            setHistoryModalHolder(holder);
                                            setHistoryModalCurrentPage(1);
                                            setShowMonthlyFeeHistoryModal(true);
                                          }}
                                          aria-label={t('monthlyFeeHistory')}
                                          title={t('monthlyFeeHistory')}
                                        >
                                          <svg viewBox="0 0 24 24" width="14" height="14" focusable="false" aria-hidden="true">
                                            <path d="M12 6.2a5.8 5.8 0 1 1-5.4 7.8" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
                                            <path d="M7.3 4.9v3.1h3.1" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M12 9.2v3.1l2 1.2" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
                                          </svg>
                                        </Button>
                                      )}
                                    </div>
                                    <div className="text-muted small">{t('totalNotesAmount')}: {formatCurrency(activeCreditTotal)}</div>
                                  </div>
                                </td>
                                <td>{(holder.keywords || []).join(', ') || '-'}</td>
                                <td>{formatDate(holder.createdDate)}</td>
                                <td className="standard-grid-action-cell">
                                  <div className="standard-grid-action-wrap">
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => handleOpenCreditNotesModal(holder)}
                                      title={t('notes')}
                                    >
                                      {t('notes')}
                                    </Button>
                                    <Button
                                      variant={holder.isActive ? 'danger' : 'success'}
                                      size="sm"
                                      onClick={() => handleToggleStatus(holder)}
                                    >
                                      {holder.isActive ? t('deactivate') : t('activate')}
                                    </Button>
                                  </div>
                                </td>
                                <td className="account-holders-edit-column-cell">
                                  <div className="standard-grid-action-wrap">
                                    <Button
                                      variant="warning"
                                      size="sm"
                                      className="account-holders-edit-btn"
                                      onClick={() => handleShowModal(holder)}
                                      aria-label={t('edit')}
                                      title={t('edit')}
                                    >
                                      <svg viewBox="0 0 24 24" className="account-holders-edit-btn-icon" focusable="false" aria-hidden="true">
                                        <path d="M5.5 3.5h9.75" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                        <path d="M5.5 3.5a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3V9.25" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M14.2 5.3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                                        <path d="M8 16.5l1-3.9 7.95-7.95a1.45 1.45 0 0 1 2.05 0l.35.35a1.45 1.45 0 0 1 0 2.05L11.4 15l-3.4 1.5z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    </Button>
                                  </div>
                                </td>
                            </tr>
                              );
                            })()}
                          </tbody>
                        </Table>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            )}

            <div className="account-holders-table-footer d-flex justify-content-between align-items-center">
              <div className="account-holders-table-meta text-muted">
                {t('showing')} {pageStart}-{pageEnd} {t('of')} {holders.length}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0 account-holders-rows-label">{t('rows')}</Form.Label>
                <Form.Select
                  className="account-holders-rows-select"
                  value={pageSize}
                  onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                  disabled={loading}
                  style={{ width: '90px' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </Form.Select>
                <Pagination className="mb-0 account-holders-pagination">
                  <Pagination.First onClick={() => setCurrentPage(1)} disabled={safeCurrentPage === 1 || loading} />
                  <Pagination.Prev onClick={() => setCurrentPage((page) => Math.max(1, page - 1))} disabled={safeCurrentPage === 1 || loading} />
                  <Pagination.Item active>{safeCurrentPage}</Pagination.Item>
                  {totalPages > 1 && <Pagination.Item disabled>/ {totalPages}</Pagination.Item>}
                  <Pagination.Next
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={safeCurrentPage === totalPages || loading}
                  />
                  <Pagination.Last onClick={() => setCurrentPage(totalPages)} disabled={safeCurrentPage === totalPages || loading} />
                </Pagination>
              </div>
            </div>
          </>
        )}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton={!isSaving}>
          <Modal.Title>
            {editingId ? `${t('edit')} ${t('accountHoldersTitle')}` : t('addNewAccountHolder')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={isSaving}>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t('name')}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleFormChange}
                onBlur={() => setAccountFormValidated(true)}
                isInvalid={accountFormValidated && formData.name.trim().length === 0}
              />
              <Form.Control.Feedback type="invalid">{t('fieldRequired')}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">{t('email')}</Form.Label>
                <Button variant="outline-secondary" size="sm" onClick={handleAddEmailField}>
                  {t('add')}
                </Button>
              </div>
              <div className="account-holder-address-editor-list">
                {(formData.emails || []).map((email, index) => (
                  <InputGroup className="mb-2" key={`email-${index}`}>
                    <Form.Control
                      type="email"
                      value={email}
                      onChange={(e) => handleEmailChange(index, e.target.value)}
                      onBlur={() => setAccountFormValidated(true)}
                      isInvalid={accountFormValidated && (!(email || '').trim() || !isValidEmail(email) || duplicateEmailIndexes.has(index))}
                    />
                    <Button
                      variant="outline-danger"
                      onClick={() => handleRemoveEmailField(index)}
                      disabled={(formData.emails || []).length === 1 && !email.trim()}
                    >
                      {t('delete')}
                    </Button>
                    {accountFormValidated && !(email || '').trim() && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {t('fieldRequired')}
                      </Form.Control.Feedback>
                    )}
                    {accountFormValidated && !!(email || '').trim() && !isValidEmail(email) && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {t('validEmailRequired')}
                      </Form.Control.Feedback>
                    )}
                    {accountFormValidated && !!(email || '').trim() && duplicateEmailIndexes.has(index) && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {t('duplicateEmailsNotAllowed')}
                      </Form.Control.Feedback>
                    )}
                  </InputGroup>
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('phoneNumber')}</Form.Label>
              <Form.Control
                type="text"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="mb-0">{t('addresses')}</Form.Label>
                <Button variant="outline-secondary" size="sm" onClick={handleAddAddressField}>
                  {t('addAddress')}
                </Button>
              </div>
              <div className="account-holder-address-editor-list">
                {formData.addressFees.map((addressFee, index) => (
                  <InputGroup className="mb-2" key={`address-${index}`}>
                    <Form.Control
                      type="text"
                      value={addressFee.address}
                      onChange={(e) => handleAddressChange(index, e.target.value)}
                      isInvalid={accountFormValidated && (!(addressFee.address || '').trim() || duplicateAddressIndexes.has(index))}
                    />
                    <Form.Control
                      type="number"
                      min={0}
                      step="0.01"
                      value={addressFee.monthlyFee}
                      onChange={(e) => handleAddressMonthlyFeeChange(index, e.target.value)}
                    />
                    <Button
                      variant="outline-danger"
                      onClick={() => handleRemoveAddressField(index)}
                      disabled={formData.addressFees.length === 1 && !addressFee.address.trim()}
                    >
                      {t('delete')}
                    </Button>
                    {accountFormValidated && !(addressFee.address || '').trim() && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {t('addressLineCannotBeBlank')}
                      </Form.Control.Feedback>
                    )}
                    {accountFormValidated && !!(addressFee.address || '').trim() && duplicateAddressIndexes.has(index) && (
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {t('duplicateAddressesNotAllowed')}
                      </Form.Control.Feedback>
                    )}
                  </InputGroup>
                ))}
              </div>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('monthlyFee')}</Form.Label>
              <Form.Control
                type="number"
                min={0}
                step="0.01"
                name="monthlyFee"
                value={Number(formData.addressFees.reduce((sum, item) => sum + (item.monthlyFee || 0), 0).toFixed(2))}
                readOnly
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t('keywords')}</Form.Label>
              <Form.Control
                type="text"
                value={keywordsInput}
                onChange={(e) => setKeywordsInput(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="account-holder-active-switch"
                name="isActive"
                label={formData.isActive ? t('active') : t('inactive')}
                checked={formData.isActive}
                onChange={handleFormChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSaveAccount} disabled={isSaving || !isAccountHolderFormValid()} className="d-inline-flex align-items-center gap-2">
            {isSaving && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
            {isSaving ? t('savingAction') : editingId ? t('update') : t('add')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showMonthlyFeeHistoryModal}
        onHide={() => {
          setShowMonthlyFeeHistoryModal(false);
          setHistoryModalHolder(null);
          setHistoryModalCurrentPage(1);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {historyModalHolder ? `${historyModalHolder.name} - ${t('monthlyFeeHistory')}` : t('monthlyFeeHistory')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historyModalEntries.length === 0 ? (
            <Alert variant="info" className="mb-0">{t('noAccountHoldersFound')}</Alert>
          ) : (
            <div className="table-responsive">
              <Table size="sm" className="mb-0">
                <thead>
                  <tr>
                    <th>{t('effectiveFromMonth')}</th>
                    <th>{t('changedAt')}</th>
                    <th>{t('linkedAddressesWithFees')}</th>
                    <th className="text-end">{t('monthlyFee')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedHistoryModalEntries.map((entry, historyIndex) => (
                    <tr key={`${historyModalHolder?.id || 'holder'}-fee-history-${historyIndex}`}>
                      <td>{formatMonthYear(entry.effectiveFromMonth)}</td>
                      <td>{formatDate(entry.changedAt)}</td>
                      <td>
                        <div className="account-holder-address-lines">
                          {entry.addressFees.map((item, addressIndex) => (
                            <div key={`${historyModalHolder?.id || 'holder'}-history-${historyIndex}-${addressIndex}`} className="account-holder-address-line">
                              <span className="account-holder-address-line-text">{item.address}</span>
                              <span className="account-holder-address-line-fee">{formatCurrency(item.monthlyFee)}</span>
                            </div>
                          ))}
                        </div>
                      </td>
                      <td className="text-end fw-semibold">{formatCurrency(entry.totalMonthlyFee)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          {historyModalEntries.length > 0 && (
            <div className="d-flex w-100 justify-content-between align-items-center gap-3 flex-wrap">
              <div className="text-muted small">
                {t('showing')} {historyModalPageStart}-{historyModalPageEnd} {t('of')} {historyModalEntries.length}
              </div>
              <div className="d-flex align-items-center gap-2">
                <Form.Label className="mb-0">{t('rows')}</Form.Label>
                <Form.Select
                  size="sm"
                  value={historyModalPageSize}
                  onChange={(e) => {
                    setHistoryModalPageSize(parseInt(e.target.value, 10));
                    setHistoryModalCurrentPage(1);
                  }}
                  style={{ width: '90px' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </Form.Select>
                <Pagination size="sm" className="mb-0">
                  <Pagination.First onClick={() => setHistoryModalCurrentPage(1)} disabled={safeHistoryModalCurrentPage === 1} />
                  <Pagination.Prev onClick={() => setHistoryModalCurrentPage((page) => Math.max(1, page - 1))} disabled={safeHistoryModalCurrentPage === 1} />
                  <Pagination.Item active>{safeHistoryModalCurrentPage}</Pagination.Item>
                  {historyModalTotalPages > 1 && <Pagination.Item disabled>/ {historyModalTotalPages}</Pagination.Item>}
                  <Pagination.Next
                    onClick={() => setHistoryModalCurrentPage((page) => Math.min(historyModalTotalPages, page + 1))}
                    disabled={safeHistoryModalCurrentPage === historyModalTotalPages}
                  />
                  <Pagination.Last onClick={() => setHistoryModalCurrentPage(historyModalTotalPages)} disabled={safeHistoryModalCurrentPage === historyModalTotalPages} />
                </Pagination>
              </div>
            </div>
          )}
          <Button
            variant="secondary"
            onClick={() => {
              setShowMonthlyFeeHistoryModal(false);
              setHistoryModalHolder(null);
              setHistoryModalCurrentPage(1);
            }}
          >
            {t('close')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCreditNotesModal}
        onHide={() => {
          if (creditNoteSubmitting) return;
          setShowCreditNotesModal(false);
          setCreditNotesModalHolder(null);
          setEditingCreditNoteId(null);
          setCreditNoteAmount('');
          setCreditNoteDescription('');
          setCreditNotesCurrentPage(1);
        }}
        size="lg"
        centered
      >
        <Modal.Header closeButton={!creditNoteSubmitting}>
          <Modal.Title>
            {creditNotesModalHolder ? `${creditNotesModalHolder.name} - ${t('notes')}` : t('notes')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body aria-busy={creditNoteSubmitting}>
          <Form className="mb-3">
            <div className="row g-3 align-items-end">
              <div className="col-12">
                <Form.Label>{t('description')}</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  value={creditNoteDescription}
                  onChange={(e) => setCreditNoteDescription(e.target.value)}
                  disabled={creditNoteSubmitting}
                />
              </div>
              <div className="col-md-4">
                <Form.Label>{t('amount')} {t('optional')}</Form.Label>
                <Form.Control
                  ref={creditNoteAmountInputRef}
                  type="number"
                  min={0}
                  step="0.01"
                  value={creditNoteAmount}
                  onChange={(e) => setCreditNoteAmount(e.target.value)}
                  disabled={creditNoteSubmitting}
                />
              </div>
              <div className="col-md-8 d-flex justify-content-md-end">
                {editingCreditNoteId && (
                  <Button
                    variant="outline-secondary"
                    onClick={handleCancelEditCreditNote}
                    disabled={creditNoteSubmitting}
                    className="me-2"
                  >
                    {t('cancel')}
                  </Button>
                )}
                <Button
                  variant="primary"
                  onClick={handleAddCreditNote}
                  disabled={creditNoteSubmitting}
                  className="d-inline-flex align-items-center gap-2"
                >
                  {creditNoteSubmitting && <LoadingIndicator label={t('savingAction')} inline compact showLines={false} />}
                  {editingCreditNoteId ? t('update') : t('add')}
                </Button>
              </div>
            </div>
          </Form>

          <div className="table-responsive mb-3">
            <Table size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>{t('amount')}</th>
                  <th>{t('description')}</th>
                  <th>{t('createdDateLabel')}</th>
                  <th>{t('actions')}</th>
                  <th className="account-holders-edit-column-header" aria-label={t('edit')}></th>
                </tr>
              </thead>
              <tbody>
                {pagedCreditNotes.map((note) => (
                  <tr key={note.id}>
                    <td className="fw-semibold">{note.amount > 0 ? formatCurrency(note.amount) : '-'}</td>
                    <td>{note.description}</td>
                    <td>{formatDate(note.createdAt)}</td>
                    <td className="standard-grid-action-cell">
                      <div className="standard-grid-action-wrap">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          disabled={creditNoteSubmitting}
                          onClick={() => handleRequestDeleteCreditNote(note)}
                        >
                          {t('delete')}
                        </Button>
                      </div>
                    </td>
                    <td className="account-holders-edit-column-cell">
                      <div className="standard-grid-action-wrap">
                      <Button
                        variant="warning"
                        size="sm"
                        className="account-holders-edit-btn"
                        disabled={creditNoteSubmitting}
                        onClick={() => handleStartEditCreditNote(note)}
                        aria-label={t('edit')}
                        title={t('edit')}
                      >
                        <svg viewBox="0 0 24 24" className="account-holders-edit-btn-icon" focusable="false" aria-hidden="true">
                          <path d="M5.5 3.5h9.75" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                          <path d="M5.5 3.5a3 3 0 0 0-3 3V18a3 3 0 0 0 3 3h11a3 3 0 0 0 3-3V9.25" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M14.2 5.3l4.5 4.5" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" />
                          <path d="M8 16.5l1-3.9 7.95-7.95a1.45 1.45 0 0 1 2.05 0l.35.35a1.45 1.45 0 0 1 0 2.05L11.4 15l-3.4 1.5z" fill="none" stroke="currentColor" strokeWidth="1.55" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {creditNotesTotal === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center text-muted">{t('noNotesFound')}</td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
            <div className="text-muted small">{t('showing')} {creditNotesPageStart}-{creditNotesPageEnd} {t('of')} {creditNotesTotal}</div>
            <div className="d-flex align-items-center gap-2">
              <Form.Label className="mb-0">{t('rows')}</Form.Label>
              <Form.Select
                size="sm"
                value={creditNotesPageSize}
                onChange={(e) => {
                  setCreditNotesPageSize(parseInt(e.target.value, 10));
                  setCreditNotesCurrentPage(1);
                }}
                style={{ width: '90px' }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </Form.Select>
              <Pagination size="sm" className="mb-0">
                <Pagination.First onClick={() => setCreditNotesCurrentPage(1)} disabled={safeCreditNotesCurrentPage === 1} />
                <Pagination.Prev onClick={() => setCreditNotesCurrentPage((page) => Math.max(1, page - 1))} disabled={safeCreditNotesCurrentPage === 1} />
                <Pagination.Item active>{safeCreditNotesCurrentPage}</Pagination.Item>
                {creditNotesTotalPages > 1 && <Pagination.Item disabled>/ {creditNotesTotalPages}</Pagination.Item>}
                <Pagination.Next onClick={() => setCreditNotesCurrentPage((page) => Math.min(creditNotesTotalPages, page + 1))} disabled={safeCreditNotesCurrentPage === creditNotesTotalPages} />
                <Pagination.Last onClick={() => setCreditNotesCurrentPage(creditNotesTotalPages)} disabled={safeCreditNotesCurrentPage === creditNotesTotalPages} />
              </Pagination>
            </div>
          </div>

        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowCreditNotesModal(false);
              setCreditNotesModalHolder(null);
              setEditingCreditNoteId(null);
              setCreditNoteAmount('');
              setCreditNoteDescription('');
              setCreditNotesCurrentPage(1);
            }}
          >
            {t('cancel')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEmailModal}
        onHide={handleAttemptClose}
        dialogClassName="email-modal-dialog-step2"
        centered
      >
        <Modal.Header className="email-modal-header" closeButton={!sendingEmail}>
          <Modal.Title>{t('composeSendEmail')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="email-modal-body" aria-busy={sendingEmail}>
          <div className="email-wizard-step">
            {/* Recipients Selection as Tag List */}
            <div className="email-compose-recipients-summary mb-3">
              <div className="email-compose-summary-label">{t('sendingToLabel')}</div>
              <div className="recipient-tag-list">
                {selectedRecipientIds.length === 0 && (
                  <span className="text-muted">{t('noRecipientsSelected')}</span>
                )}
                {selectedRecipientIds.map(id => {
                  const holder = holders.find(h => h.id === id);
                  if (!holder) return null;
                  return (
                    <button
                      type="button"
                      key={id}
                      className="recipient-tag recipient-tag-colored recipient-tag-chip"
                      title={holder.name}
                      tabIndex={0}
                      style={{ display: 'inline-flex', alignItems: 'center', border: 'none', background: 'none', padding: 0, margin: 0 }}
                      onClick={() => setSelectedRecipientIds(selectedRecipientIds.filter(rid => rid !== id))}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedRecipientIds(selectedRecipientIds.filter(rid => rid !== id));
                        }
                      }}
                    >
                      <span>{holder.name} <span className="recipient-tag-email">&lt;{getHolderEmails(holder).join(', ')}&gt;</span></span>
                      <span
                        role="button"
                        tabIndex={-1}
                        className="recipient-tag-remove recipient-tag-chip-remove"
                        aria-label={`Remove ${holder.name}`}
                        style={{ pointerEvents: 'none' }}
                      >
                        ×
                      </span>
                    </button>
                  );
                })}
                {holders.length > 1 && selectedRecipientIds.length < holders.length && (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="recipient-tag-add-all"
                    onClick={() => setSelectedRecipientIds(holders.map(h => h.id))}
                    style={{ marginLeft: '0.5rem' }}
                  >
                    {t('selectAll')}
                  </Button>
                )}
              </div>
              {/* Dropdown to add recipients */}
              <Form.Select
                className="recipient-tag-select mt-2"
                value=""
                onChange={e => {
                  const id = parseInt(e.target.value, 10);
                  if (!selectedRecipientIds.includes(id)) {
                    setSelectedRecipientIds([...selectedRecipientIds, id]);
                  }
                }}
                disabled={sendingEmail}
              >
                <option value="">{t('addRecipientPlaceholder')}</option>
                {holders
                  .filter(h => !selectedRecipientIds.includes(h.id))
                  .map(h => (
                    <option key={h.id} value={h.id}>
                      {h.name} &lt;{getHolderEmails(h).join(', ')}&gt;
                    </option>
                  ))}
              </Form.Select>
            </div>
            {/* Subject */}
            <div className="email-subject-wrapper">
              <div className="email-subject-label">
                <span>{t('subjectRequired')}</span>
                <span
                  className={`email-subject-counter ${
                    emailSubject.length > 100 ? 'warning' : emailSubject.length > 150 ? 'danger' : ''
                  }`}
                >
                  {emailSubject.length}/150
                </span>
              </div>
              <Form.Control
                className="email-subject-input"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value.slice(0, 150))}
                placeholder={t('emailSubjectPlaceholder')}
                disabled={sendingEmail}
                maxLength={150}
              />
            </div>
            {/* Message Body */}
            <div className="mt-3">
              <Form.Label className="email-message-label">{t('messageRequired')}</Form.Label>
              <div className="email-editor-container">
                <ReactQuill
                  theme="snow"
                  value={emailBodyHtml}
                  onChange={setEmailBodyHtml}
                  readOnly={sendingEmail}
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'underline'],
                      [{ header: [1, 2, 3, false] }],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['link', 'blockquote'],
                      ['clean'],
                    ],
                  }}
                />
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="email-modal-footer d-flex justify-content-between align-items-center">
          <div className="email-footer-actions">
            <Button
              variant="secondary"
              onClick={handleAttemptClose}
              disabled={sendingEmail}
            >
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              className="d-inline-flex align-items-center gap-2"
              onClick={async () => {
                try {
                  if (selectedRecipientIds.length === 0) {
                    notificationService.error(t('selectRecipientBeforeSending'));
                    return;
                  }
                  if (!emailSubject.trim()) {
                    notificationService.error(t('enterEmailSubject'));
                    return;
                  }
                  if (!emailBodyHtml.trim().replace(/<[^>]*>/g, '')) {
                    notificationService.error(t('enterEmailMessage'));
                    return;
                  }
                  setSendingEmail(true);
                  // Professional email template
                  const professionalBodyHtml = `
                    <!DOCTYPE html>
                    <html>
                    <head>
                      <meta charset='utf-8'>
                      <style>
                        body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
                        h1 { color: #2f6f62; font-size: 28px; margin: 20px 0 10px 0; }
                        .container { max-width: 700px; margin: 0 auto; padding: 20px; }
                        .header { padding: 20px; border-bottom: 3px solid #33af9a; margin-bottom: 20px; }
                        .footer { margin-top: 30px; padding-top: 20px; border-top: 1px solid #d2e3dd; text-align: center; color: #69847e; font-size: 12px; }
                      </style>
                    </head>
                    <body>
                      <div class='container'>
                        <div class='header'>
                          <h1>${t('appTitle').toUpperCase()}</h1>
                        </div>
                        <p>${t('emailGreeting')}${selectedAccountHolder?.name ? ` <strong>${selectedAccountHolder.name}</strong>` : ''},</p>
                        <div>${emailBodyHtml}</div>
                        <div class='footer'>
                          ${t('emailFooterNoReply')}
                        </div>
                      </div>
                    </body>
                    </html>
                  `;
                  const payload = {
                    accountHolderIds: selectedRecipientIds,
                    subject: emailSubject,
                    bodyHtml: professionalBodyHtml,
                  };
                  await accountHolderService.sendCustomEmail(payload);
                  notificationService.success(t('customEmailSent'));
                  setShowEmailModal(false);
                  setEmailSubject('');
                  setEmailBodyHtml('');
                  setSelectedRecipientIds([]);
                } catch (err) {
                  notificationService.error(t('failedToSendCustomEmail'));
                  console.error(err);
                } finally {
                  setSendingEmail(false);
                }
              }}
              disabled={sendingEmail}
            >
              {sendingEmail && <LoadingIndicator label={t('sendingAction')} inline compact showLines={false} />}
              {sendingEmail ? t('sendingAction') : t('send')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      {confirmationConfig && (
        <ConfirmationModal
          show={showConfirmation}
          title={confirmationConfig.title}
          message={confirmationConfig.message}
          onConfirm={() => {
            confirmationConfig.onConfirm();
          }}
          onCancel={() => {
            setShowConfirmation(false);
            setConfirmationConfig(null);
          }}
          confirmText={confirmationConfig.confirmText}
          cancelText={confirmationConfig.cancelText}
          variant={confirmationConfig.variant}
          isLoading={confirmationConfig.isLoading}
        />
      )}

      {/* Restore draft prompt (shown when modal opens and a draft exists) */}
      <ConfirmationModal
        show={showRestorePrompt}
        title="Draft Found"
        message="A draft email was found. Would you like to restore it?"
        onConfirm={() => {
          loadEmailDraft();
          setShowRestorePrompt(false);
        }}
        onCancel={() => {
          clearEmailDraft();
          setShowRestorePrompt(false);
        }}
        confirmText="Restore"
        cancelText="Discard"
        variant="info"
      />

      {/* Draft warning modal */}
      <ConfirmationModal
        show={showDraftWarning}
        title="Unsaved Changes"
        message="You have unsent changes. Would you like to save a draft before closing?"
        onConfirm={() => {
          saveEmailDraft();
          setShowDraftWarning(false);
          setShowEmailModal(false);
        }}
        onCancel={() => setShowDraftWarning(false)}
        confirmText="Save Draft"
        cancelText="Continue Editing"
        variant="warning"
      />
    </Container>
  );
};

export default AccountHolders;
